/**
 * The appearance helper: the ten lines every game on the house theme
 * (`theme.css`) would otherwise write. The house look has two appearances,
 * dark (no attribute: what the bare `:root` paints) and light
 * (`data-appearance="light"` on <html>), chosen by the player and never by
 * the OS. What a player who has chosen nothing gets is the GAME's choice:
 * dark unless the game opts in to light (as in 1.3.0, so an update never
 * repaints a game). The house RECOMMENDS light (`DEFAULT_APPEARANCE`, the
 * owner's decision of 2026-10-04: "make the default theme white"); a game
 * follows it with `data-default="light"` on the boot script and
 * `DEFAULT_APPEARANCE` as the fallback it passes here. A stored choice
 * always wins.
 *
 * Two halves:
 *
 * - BEFORE FIRST PAINT, `appearance-boot.js` (exported as
 *   `@artibens/app-shell/appearance-boot.js`): a classic script the game
 *   serves from its own origin (copy it into `public/` at build, so a strict
 *   `script-src 'self'` allows it) and loads blocking in <head>, naming its
 *   storage key:
 *
 *       <script src="/appearance-boot.js" data-key="mygame:appearance" data-default="light"></script>
 *
 *   It reads the key and stamps the attribute, so a light player never sees
 *   a dark frame first. `data-default` (since 1.4.0) is what nothing stored
 *   means: `light` or `dark`, absent = dark. It is the only piece of this file that runs before
 *   the bundle, and it does nothing else.
 * - AT RUNTIME, these functions: `readStoredAppearance(key)` and
 *   `storeAppearance(key, appearance)` for the setting, `applyAppearance`
 *   to stamp it and recolour `<meta name="theme-color">` from the ground the
 *   theme now paints, and `currentAppearance()` to read what is shown.
 *
 * A game with palettes of its own (TwinTen's six themes) does not use this:
 * it stamps its own attributes and maps `--ui-*` itself (`tokens.css`).
 */
import type { Appearance } from './types.js';

export const APPEARANCE_ATTRIBUTE = 'data-appearance';

/**
 * The house's RECOMMENDED default (since 1.4.0): light, the owner's decision
 * of 2026-10-04 ("make the default theme white"). Not applied by itself: a
 * game opts in by passing it as the fallback below and by setting
 * `data-default="light"` on `appearance-boot.js`.
 */
export const DEFAULT_APPEARANCE: Appearance = 'light';

/**
 * The appearance a stored value names: `'dark'` and `'light'` name
 * themselves; anything else (nothing, garbage) is `fallback`, dark unless
 * the game passes another (`DEFAULT_APPEARANCE` to follow the house).
 */
export function parseAppearance(
  value: string | null | undefined,
  fallback: Appearance = 'dark',
): Appearance {
  if (value === 'dark' || value === 'light') return value;
  return fallback;
}

/** The setting stored under `key`, or `fallback` (dark by default) when there is none or storage is refused. */
export function readStoredAppearance(key: string, fallback: Appearance = 'dark'): Appearance {
  try {
    return parseAppearance(globalThis.localStorage?.getItem(key), fallback);
  } catch {
    return fallback;
  }
}

/** Remember the player's choice under `key`. A refused write is not an error. */
export function storeAppearance(key: string, appearance: Appearance): void {
  try {
    globalThis.localStorage?.setItem(key, appearance);
  } catch {
    // Private mode or a full quota: the choice still applies for this visit.
  }
}

/** What <html> shows now: light with the attribute, dark without it. */
export function currentAppearance(root: HTMLElement = document.documentElement): Appearance {
  return root.getAttribute(APPEARANCE_ATTRIBUTE) === 'light' ? 'light' : 'dark';
}

/**
 * Show `appearance`: stamp (or remove) the attribute on <html>, and set
 * `<meta name="theme-color">` to the ground colour the theme now paints, so
 * a phone's status bar follows. Returns that colour ('' when it cannot be
 * read, as in a test without the theme's stylesheet).
 */
export function applyAppearance(
  appearance: Appearance,
  root: HTMLElement = document.documentElement,
): string {
  if (appearance === 'light') root.setAttribute(APPEARANCE_ATTRIBUTE, 'light');
  else root.removeAttribute(APPEARANCE_ATTRIBUTE);
  const ground = getComputedStyle(root).getPropertyValue('--ui-ground').trim();
  if (ground !== '') {
    const meta = root.ownerDocument.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    meta?.setAttribute('content', ground);
  }
  return ground;
}
